import React, { useState } from 'react';
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { APPLICATION_UPDATE_DETAILS, GET_APP_CATEGORIES } from './apps.graphql';

/**
 * Edits an app's title, description and category. They live in Apps and readers see them there, so
 * they are saved together, in one go, rather than field by field on the page.
 */
export function AppDetailsDialog({
  app,
  onClose,
}: {
  app: { id: string; name?: string | null; description?: string | null; category?: string | null };
  onClose(saved: boolean): void;
}) {
  const { data: categoryData } = useQuery(GET_APP_CATEGORIES, { fetchPolicy: 'cache-and-network' });
  const categories = [...new Set((categoryData?.appCategories ?? []).filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b));
  const [name, setName] = useState(app.name ?? '');
  const [description, setDescription] = useState(app.description ?? '');
  const [category, setCategory] = useState(app.category ?? '');
  const [touched, setTouched] = useState(false);
  const [update, { loading, error }] = useMutation(APPLICATION_UPDATE_DETAILS);

  const nameMissing = name.trim() === '';
  const categoryMissing = category.trim() === '';
  // Not until the categories have loaded, or every category would look new for a moment.
  const isNewCategory = !!categoryData && !categoryMissing && !categories.some((c) => c.toLowerCase() === category.trim().toLowerCase());

  const handleSave = async () => {
    setTouched(true);
    if (nameMissing || categoryMissing) return;
    try {
      await update({ variables: { input: { appId: app.id, name, description, category } } });
      onClose(true);
    } catch {
      // The error is shown in the dialog from the mutation's state.
    }
  };

  return (
    <Dialog open={true} onClose={() => onClose(false)} maxWidth="sm" fullWidth={true}>
      <DialogTitle>Rediger applikasjon</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ mt: 1 }}>
          <TextField
            label="Tittel"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={touched && nameMissing}
            helperText={touched && nameMissing ? 'Skriv inn en tittel' : undefined}
            fullWidth
            autoFocus
          />
          <TextField label="Beskrivelse" value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={3} fullWidth />
          {/* Pick one the catalog uses or write a new one. Apps keeps the existing spelling for one that
              differs only in case, so the hint for a new category only shows for a genuinely new one. */}
          <Autocomplete
            freeSolo
            options={categories}
            inputValue={category}
            onInputChange={(_, value) => setCategory(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Kategori"
                error={touched && categoryMissing}
                helperText={touched && categoryMissing ? 'Velg eller skriv inn en kategori' : isNewCategory ? 'Ny kategori' : undefined}
              />
            )}
          />
          {error && <Alert severity="error">Kunne ikke lagre endringene. Prøv igjen.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Avbryt</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          Lagre
        </Button>
      </DialogActions>
    </Dialog>
  );
}
